import { ActionAfterViewObserver } from '@cian/action-after-viewed-component';
import { ErrorLogComponent } from '@cian/error-log-component';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { useApplicationContext } from 'shared/containers/ApplicationContext';
import { NewbuildingMortgageCalculatorMicrofrontend } from 'shared/containers/NewbuildingMortgageCalculatorMicrofrontend';
import { selectMortgageMiniCalculatorEnabled } from 'shared/selectors/featureToggle';

import { calculateBannerMonthlyPayment, debounceTracking, getInitialFirstPayment } from './helpers';
import { CianMortgageBannerContainer } from './parts/CianMortgageBannerContainer';
import { MortgageOffersTableContainer } from './parts/MortgageOffersTableContainer';
import { trackInitialPaymentFilterChange, trackLoanTermFilterChange, trackPromoOffersShow } from './tracking';
import { MortgageOffersFilters, NewbuildingMortgageSection } from '../../components/NewbuildingMortgageSection';
import { INITIAL_LOAN_TERM } from '../../constants/newbuildingMortgage';
import { selectIsMortgageBrokerBannerAvailable } from '../../selectors/mortgageBroker';
import { getOffer } from '../../selectors/newObject';
import { selectOfferPriceRub } from '../../selectors/offerData';
import { IPromoInfo } from '../../types/newbuildingPromos';

export interface INewbuildingMortgageSectionContainerProps {
  header: string;
  showHeader?: boolean;
}

export const NewbuildingMortgageSectionContainer: React.FC<
  React.PropsWithChildren<INewbuildingMortgageSectionContainerProps>
> = ({ header, showHeader }) => {
  const { logger } = useApplicationContext();
  const price = useSelector(selectOfferPriceRub);
  const isMortgageBrokerBannerAvailable = useSelector(selectIsMortgageBrokerBannerAvailable);
  const isNewMortgageCalculatorMicrofrontEnabled = useSelector(selectMortgageMiniCalculatorEnabled);

  const [initialPayment, setInitialPayment] = React.useState(getInitialFirstPayment(price));
  const [loanTerm, setLoanTerm] = React.useState(INITIAL_LOAN_TERM);
  const [offersCount, setOffersCount] = React.useState(0);
  const displayedOffersRef = React.useRef<IPromoInfo[]>([]);

  const bannerMonthlyPayment = calculateBannerMonthlyPayment({
    price,
    loanTerm,
    firstPayment: initialPayment,
    initialFirstPayment: getInitialFirstPayment(price),
    offersCount,
  });

  const offer = useSelector(getOffer);

  const trackInitialPaymentFilterChangeDebounced = React.useCallback(
    debounceTracking(trackInitialPaymentFilterChange),
    [],
  );
  const trackLoanTermFilterChangeDebounced = React.useCallback(debounceTracking(trackLoanTermFilterChange), []);

  const handleShow = React.useCallback(() => {
    trackPromoOffersShow({ promos: displayedOffersRef.current, offer, isMortgagePromos: true });
  }, [offer]);

  const handleDisplayedOffersChange = React.useCallback((displayedOffers: IPromoInfo[]) => {
    displayedOffersRef.current = displayedOffers;
  }, []);

  const handleInitialPaymentChange = React.useCallback(
    (value: number) => {
      setInitialPayment(value);
      trackInitialPaymentFilterChangeDebounced();
    },
    [trackInitialPaymentFilterChangeDebounced],
  );

  const handleLoanTermChange = React.useCallback(
    (value: number) => {
      setLoanTerm(value);
      trackLoanTermFilterChangeDebounced();
    },
    [trackLoanTermFilterChangeDebounced],
  );

  if (isMortgageBrokerBannerAvailable) {
    return null;
  }

  if (isNewMortgageCalculatorMicrofrontEnabled) {
    return (
      <ActionAfterViewObserver callback={handleShow} triggerOnce>
        <NewbuildingMortgageSection header={header} showHeader={false}>
          <ErrorLogComponent logger={logger}>
            <NewbuildingMortgageCalculatorMicrofrontend />
          </ErrorLogComponent>
        </NewbuildingMortgageSection>
      </ActionAfterViewObserver>
    );
  }

  return (
    <ActionAfterViewObserver callback={handleShow} triggerOnce>
      <NewbuildingMortgageSection header={header} showHeader={showHeader}>
        <MortgageOffersFilters
          initialPayment={initialPayment}
          loanTerm={loanTerm}
          price={price}
          onInitialPaymentChange={handleInitialPaymentChange}
          onInitialPaymentMouseDown={trackInitialPaymentFilterChange}
          onLoanTermChange={handleLoanTermChange}
          onLoanTermMouseDown={trackLoanTermFilterChange}
        />
        <MortgageOffersTableContainer
          price={price}
          loanTerm={loanTerm}
          initialPayment={initialPayment}
          onOffersCountChange={setOffersCount}
          onDisplayedOffersChange={handleDisplayedOffersChange}
        />
        <CianMortgageBannerContainer monthlyPayment={bannerMonthlyPayment} />
      </NewbuildingMortgageSection>
    </ActionAfterViewObserver>
  );
};
